/**
 * taken from tidal/form-ui: https://github.com/WorkWave/tidal/blob/f6648e22ca390290762ab53a288c45b6d63d4242/packages/form-ui/src/fields/TimePicker/utils/convertTimeInput.ts
 */

import {
  differenceInMinutes,
  format,
  parseISO,
  startOfToday,
} from 'date-fns/esm'

type TimeFormat = 'hh:mm aa' | 'HH:mm'

// Meridiem Regex
const meridiemReg = '(?<meridiem>[AaPp])'

// Separators Regex
const separatorsReg = '[:.]'

// These three regex will match all the 24h cases
const formats24 = {
  fourDigits: `(2[0-4]|[0-1][0-9])${separatorsReg}?([0-5]?[0-9])`,
  threeDigits: `([0-9]${separatorsReg}?[0-5][0-9])`,
  lessThanThreeDigits: `([0-9]${separatorsReg}[0-9])|(2[0-4]|[0-1][0-9]|([0-9]$))`,
}

// These three regex will match all the 12h cases
const formats12 = {
  fourDigits: `(0[0-9]|1[0-2])${separatorsReg}[0-5]?[0-9])`,
  threeDigits: `([0-9]${separatorsReg}?[0-5][0-9])`,
  lessThanThreeDigits: `(1[0-2]|0[0-9]|[0-9])`,
}

// Full 24h regex
const regEx24 = `^(?<time24>${formats24.fourDigits}|${formats24.threeDigits}|${formats24.lessThanThreeDigits})`

// Full 12h regex
const regEx12 = `^(?<time12>(${formats12.fourDigits}|${formats12.threeDigits}|${formats12.lessThanThreeDigits}) ?${meridiemReg}`

// Final regex
const regEx = new RegExp(`${regEx12}|${regEx24}`)

// Cache object
const convertTimeInputCache: Map<string, number | 'invalid'> = new Map()

/**
 * This method tries to convert a time-like input to the number of minutes
 * from the midnight to the value.
 *
 * Valid formats (NOTE: meridiem case is ignored and the value can be partial => a/p/A/P):
 * 22:30 am/pm | 22:30am/pm | 22.30 am/pm | 22.30am/pm | 2230 am/pm | 2230am/pm | 4:00 am/pm
 * 1am/pm | 1 | 01 | 0100 | 100
 *
 * If the value can't be parsed the method will return `invalid`.
 */
export function convertTimeInput(
  input: string,
  defaultFormat: TimeFormat = 'HH:mm',
) {
  const timeInput = input.trim()

  // Check for cached results
  const cacheId = `${input}${defaultFormat}`
  if (convertTimeInputCache.has(cacheId)) {
    const cachedResult = convertTimeInputCache.get(cacheId)

    if (cachedResult) return cachedResult
  }

  const groups = timeInput.match(regEx)?.groups

  // If no named matching groups has been found, return 'invalid'
  if (!groups) return 'invalid'

  // If a meridiem (also partial) has been found, let's treat the string
  // as a 12h time. All the other cases can be managed with 24h format

  let timeValue = groups.meridiem ? groups.time12 : groups.time24

  // ------------------------------------------------------
  // START: Let's transform the value in a hmma/HHmm format
  // ------------------------------------------------------

  // If a separator has been found, let's split the value in hours and
  // minutes and fill it with zeroes
  const separator = timeValue.match(/[:.]/)
  if (separator) {
    const [hours, minutes] = timeValue.split(separator[0])
    timeValue = `${hours.padStart(2, '0')}${minutes.padEnd(2, '0')}`
  }
  // If no separator is present, just fill the value with zeroes
  else {
    switch (timeValue.length) {
      case 3:
        // 100 => 0100
        timeValue = `0${timeValue}`
        break
      case 2:
        // 10 => 1000
        timeValue = `${timeValue}00`
        break
      case 1:
        // 1 => 0100
        timeValue = `0${timeValue}00`
        break
    }
  }

  // That hack allows date-fns to parse correctly 24:00
  if (timeValue === '24' || timeValue === '2400') {
    timeValue = '0000'
  }

  const today = startOfToday()

  // Let's create a valid ISO-8601 time string
  let hoursString = timeValue.substr(0, 2)
  switch (groups?.meridiem) {
    case 'a':
    case 'A':
      if (hoursString === '12') {
        hoursString = '00'
      }
      timeValue = `${hoursString}${timeValue.substr(2, 2)}`
      break

    case 'p':
    case 'P':
      /**
       * If time is between 12:00 pm and 12:59 pm there's no need to add 12 hours since the time is
       * already ok (12:15 pm => 12:15 in 24h format)
       */
      if (hoursString === '12') break

      // Let's add 12 hours to match 24h format (3:00 pm => 15:00 in 24h format)
      const hours = parseInt(hoursString, 10)
      timeValue = `${hours + 12}${timeValue.substr(2, 2)}`
      break

    /**
     * Let's fix ambiguous cases where a value without a meridiem could lead
     * to some unwanted conversions like 12:00
     * that in 24h format should be converted to 12:00 (12:00pm)
     * but in 12h format should be converted to 12:00am
     */
    default:
      if (defaultFormat === 'hh:mm aa') {
        const hours = parseInt(hoursString, 10)
        if (hours === 12) {
          timeValue = `00${timeValue.substr(2, 2)}`
        }
      }
      break
  }

  const toParse = `${format(today, 'yyyy-MM-dd')}T${timeValue}`

  // ------------------------------------------------------
  // END
  // ------------------------------------------------------

  // Get the minutes from midnight to the calculated value
  const result = differenceInMinutes(parseISO(toParse), startOfToday())

  // Store the result into the cache
  convertTimeInputCache.set(cacheId, result)

  return result
}
